import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ApiContext } from "../../context/MyContext";
import { jwtDecode } from "jwt-decode";
import { useEmployeeVerification } from "../../hooks/useEmployeeVerification";
import { hasRouteAccess } from "../accessControl";
import { useUtils } from "../../context/UtilsContext";
import Loader from "../../common/Loader/Loader";

/** Empty shell only used when verify finished but API returned no record — same shape as get-current-employee payload */
const FALLBACK_EMPLOYEE_FOR_ACCESS = { data: { roles: [], departments: [] } };

const PrivateRoute = ({ element: Component }) => {
  const {
    isAuthenticated,
    login,
    employeeData,
    departmentsWithRoles,
    fetchDepartmentsWithRoles,
    fetchUserProfile,
  } = useContext(ApiContext);
  const { sessionUtils, utilsLoading } = useUtils();
  const location = useLocation();
  const token = localStorage.getItem("access_token");
  const { verifyEmployee } = useEmployeeVerification();
  const profileFetchRanRef = useRef(false);
  /** True after verifyEmployee settles (success/fail). Cached employee in MyContext can satisfy access before this. */
  const [employeeVerificationFinished, setEmployeeVerificationFinished] = useState(() => {
    try {
      return !!localStorage.getItem("current_employee_data");
    } catch {
      return false;
    }
  });
  const verifyOnceRef = useRef(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  const isTokenExpired = useMemo(() => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return true;
      const now = Date.now() / 1000;
      return decoded.exp < now;
    } catch (err) {
      return true;
    }
  }, [token]);

  useEffect(() => {
    if (token && !isAuthenticated && !isTokenExpired) {
      login();
    }
    if (isTokenExpired) {
      localStorage.removeItem("access_token");
    }
  }, [token, isAuthenticated, login, isTokenExpired]);

  // Single verify per mount, after session utils are ready (stable TOS / event ids inside hook).
  useEffect(() => {
    if (!token || isTokenExpired || !isAuthenticated) {
      setEmployeeVerificationFinished(true);
      return undefined;
    }
    const sessionGateReady = sessionUtils != null || utilsLoading === false;
    if (!sessionGateReady) {
      return undefined;
    }
    if (verifyOnceRef.current) {
      return undefined;
    }
    verifyOnceRef.current = true;
    verifyEmployee()
      .then((result) => {
        if (result?.error) {
          console.warn("Employee verification failed on mount:", result.error);
        }
      })
      .catch((err) => {
        console.error("Error during employee verification on mount:", err);
      })
      .finally(() => {
        // Always unblock — do not gate on StrictMode cleanup (avoids infinite loader).
        setEmployeeVerificationFinished(true);
      });
    return undefined;
  }, [
    token,
    isTokenExpired,
    isAuthenticated,
    sessionUtils,
    utilsLoading,
    verifyEmployee,
  ]);

  useEffect(() => {
    if (isAuthenticated && !departmentsWithRoles && fetchDepartmentsWithRoles) {
      fetchDepartmentsWithRoles();
    }
  }, [isAuthenticated, departmentsWithRoles, fetchDepartmentsWithRoles]);

  useEffect(() => {
    if (token && !isTokenExpired && isAuthenticated && sessionUtils && !profileFetchRanRef.current) {
      profileFetchRanRef.current = true;
      fetchUserProfile(sessionUtils)
        .then((result) => {
          if (!result.success) {
            console.warn("Profile fetch failed:", result.message);
          }
        })
        .catch((err) => {
          console.error("Error fetching profile on mount:", err);
        });
    }
  }, [token, isTokenExpired, isAuthenticated, sessionUtils, fetchUserProfile]);

  // Access: proceed as soon as we have employee from API/cache, OR verify has finished (fallback shell).
  useEffect(() => {
    const accessDataReady =
      employeeData != null || employeeVerificationFinished;
    if (!accessDataReady) {
      setAccessChecked(false);
      return;
    }
    const effectiveEmployee = employeeData ?? FALLBACK_EMPLOYEE_FOR_ACCESS;
    const access = hasRouteAccess(
      location.pathname,
      effectiveEmployee,
      departmentsWithRoles
    );
    setHasAccess(access);
    setAccessChecked(true);
  }, [
    employeeData,
    departmentsWithRoles,
    location.pathname,
    employeeVerificationFinished,
  ]);

  if (!token || isTokenExpired) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!accessChecked) {
    return <Loader />;
  }

  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  return <Component />;
};

export default PrivateRoute;
